import React, { Component } from "react";
import Switch from "react-switch";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import _ from "lodash";
import moment from "moment";
import FontAwesome from "react-fontawesome";
import Dropzone from "react-dropzone";
import {
	Header,
	AddButton,
	Button,
	GenericInput,
	WarningPopup,
	ImageInput,
	Popup,
	HubSidebar,
	CheckBox,
	ImageViewer,
} from "../../components";
import { validateAccess } from "../../session";
import {
	imageLibraryUpdate,
	imageFolderUpdated,
	imageFolderRemoved,
} from "../../actions";
import { fileActions } from "../../webapi";
import {
	COLOUR_BRANDING_OFF,
	COLOUR_TANGERINE,
	COLOUR_BRANDING_ACTION,
	COLOUR_LIGHTGREY,
} from "../../js";
import { get1400 } from "../../helper";
import { PlussCore } from "../../config/core";
import styles from "./ImageLibraryHub.module.css";

const NEW_FOLDER_ID = "new";

class ImageLibraryHub extends Component {
	state = {
		selectedFolder: null,
		editingFolder: null,
		deletingFolder: null,
		deletingImage: null,
		uploadingImage: false,
		uploadingImages: false,
		imagesUploaded: [],
		searchText: "",
		editingImage: null,
		editCaptionText: "",
		selectedImages: [],
		viewingImage: null,
	};

	componentDidMount() {
		if (
			!this.canManageLibrary() &&
			!this.canAddImage() &&
			!this.canAddPublishingImage()
		) {
			this.props.history.push("/mastermenu");
		} else {
			this.getImageLibrary();
		}
	}

	getImageLibrary() {
		this.props.imageLibraryUpdate(this.props.auth.site, true);
	}

	canManageLibrary = () => {
		return validateAccess(
			this.props.auth.site,
			"manageImageLibrary",
			this.props.auth,
		);
	};

	canAddImage = () => {
		return validateAccess(
			this.props.auth.site,
			"addToImageLibrary",
			this.props.auth,
		);
	};

	canAddPublishingImage = () => {
		return validateAccess(
			this.props.auth.site,
			"addToPublishingImageLibrary",
			this.props.auth,
		);
	};

	getImageStyle(displayValue) {
		if (_.isEmpty(displayValue)) return null;

		const image = get1400(displayValue);
		return { backgroundImage: `url(${image})` };
	}

	getFolderFromProps = () => {
		const { selectedFolder } = this.state;
		if (!selectedFolder) return null;

		const { imageLibrary } = this.props;
		return (
			imageLibrary.find((folder) => folder.RowId === selectedFolder.RowId) ||
			selectedFolder
		);
	};

	onSelectFolder = (selectedFolder) => {
		this.setState({
			selectedFolder,
			selectedImages: [],
		});
	};

	onSelectRoot = () => {
		this.getImageLibrary();
		this.setState({
			selectedFolder: null,
			selectedImages: [],
		});
	};

	onToggleSelectImage = (image) => {
		const { selectedImages } = this.state;
		const isSelected = selectedImages.some((img) => img.uri === image.uri);

		if (isSelected) {
			const newSelectedImages = selectedImages.filter(
				(img) => img.uri !== image.uri,
			);
			this.setState({ selectedImages: newSelectedImages });
		} else {
			this.setState({ selectedImages: [...selectedImages, image] });
		}
	};

	onNewFolder = () => {
		this.setState({ selectedFolder: null });
		const newFolder = { RowId: NEW_FOLDER_ID, Name: "", Images: [] };
		this.props.imageFolderUpdated(newFolder);
		this.onStartEditingFolder(null, newFolder);
	};

	onStartEditingFolder = (event, editingFolder) => {
		if (event) event.stopPropagation();

		this.setState({ editingFolder }, () => {
			const input = document.getElementById("addFolderInput");
			if (input) input.focus();
		});
	};

	isNewFolder = (folder) =>
		_.isNil(folder.RowId) || folder.RowId === NEW_FOLDER_ID;

	onCancelEditingFolder = () => {
		const { editingFolder } = this.state;
		if (editingFolder && this.isNewFolder(editingFolder)) {
			this.props.imageFolderRemoved(editingFolder);
		}
		this.setState({ editingFolder: null });
	};

	onEndEditingFolder = () => {
		const updatedFolder = this.state.editingFolder;
		if (_.isEmpty(updatedFolder.Name)) return;

		this.setState({ editingFolder: null }, async () => {
			try {
				this.props.imageFolderUpdated(updatedFolder);
				if (this.isNewFolder(updatedFolder)) {
					await fileActions.addMediaFolder(
						this.props.auth.site,
						updatedFolder.Name,
					);
				} else {
					await fileActions.updateMediaFolder(
						updatedFolder.RowId,
						this.props.auth.site,
						updatedFolder.Name,
					);
				}
				this.getImageLibrary();
			} catch (error) {
				console.log("onEndEditingFolder error", error);
			}
		});
	};

	onChangeFolderName = (event) => {
		const editingFolder = { ...this.state.editingFolder };
		editingFolder.Name = event.target.value;
		this.setState({ editingFolder });
	};

	onDeleteFolder = (event, deletingFolder) => {
		if (event) event.stopPropagation();
		this.setState({ deletingFolder });
	};

	onConfirmDeleteFolder = () => {
		const deletedFolder = this.state.deletingFolder;
		this.setState({ deletingFolder: null, editingFolder: null }, async () => {
			try {
				this.props.imageFolderRemoved(deletedFolder);
				await fileActions.deleteMediaFolder(
					deletedFolder.RowId,
					this.props.auth.site,
				);
				this.getImageLibrary();
			} catch (error) {
				console.log("deleteMediaFolder error", error);
			}
		});
	};

	onCancelDeleteFolder = () => {
		this.setState({ deletingFolder: null });
	};

	onDeleteImage = (event, deletingImage) => {
		if (event) event.stopPropagation();
		this.setState({ deletingImage });
	};

	onConfirmDeleteImage = () => {
		const deletingImages = Array.isArray(this.state.deletingImage)
			? this.state.deletingImage
			: [this.state.deletingImage];
		const updatedFolder = { ...this.state.selectedFolder };
		updatedFolder.Images = updatedFolder.Images.filter(
			(image) => !deletingImages.some((delImg) => delImg.uri === image.uri),
		);
		this.setState(
			{
				deletingImage: null,
				selectedFolder: updatedFolder,
				selectedImages: [],
			},
			async () => {
				try {
					this.props.imageFolderUpdated(updatedFolder);
					await fileActions.deleteImagesFromFolder(
						updatedFolder.RowId,
						this.props.auth.site,
						deletingImages,
					);
					this.getImageLibrary();
				} catch (error) {
					console.log("onConfirmDeleteImage error", error);
				}
			},
		);
	};

	onCancelDeleteImage = () => {
		this.setState({ deletingImage: null });
	};

	onViewImage = (event, list, index, title) => {
		if (event) event.stopPropagation();
		this.setState({ viewingImage: { list, index, title } });
	};

	onChangeViewingIndex = (index) => {
		this.setState((prev) => ({
			viewingImage: { ...prev.viewingImage, index },
		}));
	};

	onCloseImageViewer = () => {
		this.setState({ viewingImage: null });
	};

	onUploadImage = () => {
		this.setState({ uploadingImage: true });
	};

	onSaveUploadImage = async () => {
		const { selectedFolder, imagesUploaded } = this.state;
		if (!selectedFolder || imagesUploaded.length < 1) return;

		this.setState({ uploadingImages: true });

		try {
			// Step 1: Upload images to S3
			const uploadedImages = await Promise.all(
				imagesUploaded.map(async (img) => {
					if (img.file) {
						const url = await fileActions.uploadMediaAsync(
							img.file,
							img.file.name,
						);
						return {
							uri: url,
							caption: img.caption,
							date: img.date,
							user: img.user,
						};
					}
					return {
						uri: img.uri,
						caption: img.caption,
						date: img.date,
						user: img.user,
					};
				}),
			);

			// Step 2: Add images to folder via API
			await fileActions.addImagesToFolder(
				selectedFolder.RowId,
				this.props.auth.site,
				uploadedImages,
			);

			// Step 3: Refresh image library
			this.getImageLibrary();

			// Step 4: Close modal after all operations complete
			this.setState({
				uploadingImage: false,
				uploadingImages: false,
				imagesUploaded: [],
			});
		} catch (error) {
			console.log("onSaveUploadImage upload error", error);
			this.setState({ uploadingImages: false });
		}
	};

	onCancelUploadImage = () => {
		this.setState({ uploadingImage: false, imagesUploaded: [] });
	};

	onSearchChange = (event) => {
		const searchText = event.target.value;
		this.setState({ searchText });
	};

	onChangePublishing = (folder, isPublishing) => {
		if (!folder) return;

		const updatedFolder = { ...folder };
		updatedFolder.Published = isPublishing;
		try {
			this.props.imageFolderUpdated(updatedFolder);
			setTimeout(async () => {
				await fileActions.updateMediaFolder(
					updatedFolder.RowId,
					this.props.auth.site,
					null,
					isPublishing,
				);
				this.getImageLibrary();
			}, 500);
		} catch (error) {
			console.log("onChangePublishing error", error);
		}
	};

	onEditImage = (event, image) => {
		if (event) event.stopPropagation();
		this.setState(
			{
				editingImage: image,
				editCaptionText: image.caption || "",
			},
			() => {
				const input = document.getElementById("editCaptionInput");
				if (input) input.focus();
			},
		);
	};

	onCancelEditCaption = () => {
		this.setState({ editingImage: null, editCaptionText: "" });
	};

	onChangeEditCaption = (event) => {
		this.setState({ editCaptionText: event.target.value });
	};

	onChangeUploadCaption = (index, caption) => {
		const imagesUploaded = [...this.state.imagesUploaded];
		imagesUploaded[index].caption = caption;
		this.setState({ imagesUploaded });
	};

	onRemoveUploadedImage = (index) => {
		const imagesUploaded = [...this.state.imagesUploaded];
		const removedImage = imagesUploaded[index];
		if (removedImage.uri && removedImage.uri.startsWith("blob:")) {
			URL.revokeObjectURL(removedImage.uri);
		}
		imagesUploaded.splice(index, 1);
		this.setState({ imagesUploaded });
	};

	onDropMoreImages = (acceptedFiles) => {
		if (!acceptedFiles || acceptedFiles.length === 0) return;

		const { user } = this.props.auth;
		const newImages = acceptedFiles.map((file) => ({
			uri: URL.createObjectURL(file),
			caption: "",
			date: moment().valueOf(),
			user: {
				Id: user.Id,
				displayName: user.displayName,
				profilePic: user.profilePic,
			},
			file,
		}));

		// Filter out duplicates using existing URIs
		const existingUris = new Set(
			this.state.imagesUploaded.map((img) => img.uri),
		);
		const uniqueNewImages = newImages.filter(
			(img) => !existingUris.has(img.uri),
		);

		this.setState({
			imagesUploaded: [...this.state.imagesUploaded, ...uniqueNewImages],
		});
	};

	onSaveCaption = async () => {
		const { editingImage, editCaptionText, selectedFolder } = this.state;
		if (!editingImage || !selectedFolder) return;

		// Update local state
		const updatedFolder = { ...selectedFolder };
		updatedFolder.Images = updatedFolder.Images.map((img) => {
			if (img.uri === editingImage.uri) {
				return {
					...img,
					caption: editCaptionText.trim(),
				};
			}
			return img;
		});

		this.setState(
			{
				selectedFolder: updatedFolder,
				editingImage: null,
				editCaptionText: "",
			},
			async () => {
				try {
					this.props.imageFolderUpdated(updatedFolder);
					// Update the existing image's caption
					await fileActions.updateCaption(
						editingImage.uri,
						this.props.auth.site,
						editCaptionText.trim(),
					);
					this.getImageLibrary();
				} catch (error) {
					console.log("onSaveCaption error", error);
				}
			},
		);
	};

	renderStats(stat, loading) {
		if (loading)
			return (
				<FontAwesome className={styles.spinner} name="spinner fa-pulse fa-fw" />
			);
		return stat;
	}

	renderLeftBar() {
		const sectionItems = [];

		if (this.canManageLibrary()) {
			sectionItems.push({
				type: "newButton",
				text: "New Folder",
				onClick: this.onNewFolder,
			});
		}

		sectionItems.push({
			type: "navItem",
			text: "View Gallery",
			icon: "eye",
			isFontAwesome: true,
			selected: true,
			onClick: this.onSelectRoot,
		});

		return (
			<HubSidebar
				sections={[
					{
						title: "Gallery",
						items: sectionItems,
					},
				]}
				helpGuide={{
					text: "Help with Gallery",
					url: PlussCore.Urls.HelpGuide.Gallery,
				}}
			/>
		);
	}

	renderPublicFolder(folder, index) {
		const imageCount = folder.Images ? folder.Images.length : 0;
		const countText = `${imageCount} Image${imageCount > 0 ? "s" : ""}`;

		return (
			<div
				key={index}
				className="imageLibraryPublicFolderContainer"
				onClick={this.onSelectFolder.bind(this, folder)}
			>
				<div className="leftSection">
					<div className="folderIcon"></div>
				</div>
				<div className="rightSection">
					<div className="folderTitle">{folder.Name}</div>
					<div className="folderInfo">
						{`${countText}`}
						<div className="publishingState">
							&nbsp;
							{`• ${folder.Published ? "Published in App" : "Not Published in App"}`}
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderFolder(folder, index) {
		const { editingFolder } = this.state;
		const isEditing =
			!_.isNil(editingFolder) && editingFolder.RowId === folder.RowId;
		const imageCount = folder.Images ? folder.Images.length : 0;
		const countText = `${imageCount} Image${imageCount > 0 ? "s" : ""}`;

		const visibleSwitch = (() => {
			if (isEditing || !this.canManageLibrary() || this.isNewFolder(folder))
				return null;
			return (
				<div
					className="publishingSwitch"
					onClick={(event) => event.stopPropagation()}
				>
					<Switch
						checked={folder.Published === true}
						onChange={(checked) => this.onChangePublishing(folder, checked)}
						onColor={COLOUR_BRANDING_ACTION}
						offColor={COLOUR_LIGHTGREY}
						checkedIcon={false}
						uncheckedIcon={false}
						height={26}
						width={50}
					/>
					<p className="label">Visible in App</p>
				</div>
			);
		})();

		const editButton = (() => {
			if (!this.canManageLibrary() || this.isNewFolder(folder)) return null;
			return (
				<div
					className="editFolderContainer"
					onClick={(event) =>
						isEditing
							? this.onDeleteFolder(event, folder)
							: this.onStartEditingFolder(event, folder)
					}
				>
					<FontAwesome
						name={isEditing ? "trash" : "pencil"}
						className={"editFolderIcon"}
						style={isEditing ? { color: COLOUR_TANGERINE } : {}}
					/>
				</div>
			);
		})();

		const folderInfo = (() => {
			if (isEditing) {
				return (
					<GenericInput
						id="addFolderInput"
						type="text"
						placeholder="Enter folder name here"
						value={editingFolder.Name}
						onChange={this.onChangeFolderName}
						inputclassName={styles.addFolderInputText}
					/>
				);
			}
			return (
				<div>
					<div className="folderTitle">{folder.Name}</div>
					<div className="imageCount">{countText}</div>
				</div>
			);
		})();

		return (
			<div
				key={index}
				className={`imageLibraryFolderContainer ${isEditing ? styles.folderContainerEditing : styles.folderContainerNormal}`}
				onClick={isEditing ? null : this.onSelectFolder.bind(this, folder)}
			>
				<div className="topSection">
					{visibleSwitch}
					{editButton}
					<div className="folderIcon"></div>
				</div>
				<div className="bottomSection">{folderInfo}</div>
				{isEditing && (
					<Button
						inline
						buttonType="primary"
						onClick={this.onEndEditingFolder}
						isActive={!_.isEmpty(editingFolder.Name)}
						className="doneButton"
					>
						Done
					</Button>
				)}
			</div>
		);
	}

	renderImage(image, index, allowDelete = true, imageList = null, viewerTitle = null) {
		const { selectedImages } = this.state;
		const isSelected = selectedImages.some((img) => img.uri === image.uri);

		const checkbox = (() => {
			if (!allowDelete) return null;
			return (
				<div
					className={styles.imageCheckboxContainer}
					onClick={(e) => e.stopPropagation()}
				>
					<CheckBox
						isActive={isSelected}
						onChange={() => this.onToggleSelectImage(image)}
					/>
				</div>
			);
		})();

		const deleteButton = (() => {
			const { selectedFolder } = this.state;
			if (
				!allowDelete ||
				(selectedFolder && !selectedFolder.Public && !this.canAddImage()) ||
				(selectedFolder &&
					selectedFolder.Public &&
					!this.canAddPublishingImage())
			)
				return null;

			return (
				<div
					className="deleteImageContainer"
					onClick={(event) => this.onDeleteImage(event, image)}
				>
					<FontAwesome name="trash" className="deleteImageIcon" />
				</div>
			);
		})();

		const viewButton = (
			<div
				className="viewImageContainer"
				onClick={(event) => this.onViewImage(event, imageList, index, viewerTitle)}
				title="View full-size image"
				role="button"
				aria-label="View full-size image"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						this.onViewImage(e, imageList, index, viewerTitle);
					}
				}}
			>
				<FontAwesome name="search-plus" className="viewImageIcon" />
			</div>
		);

		const editCaptionButton = (() => {
			const { selectedFolder } = this.state;
			if (
				!allowDelete ||
				(selectedFolder && !selectedFolder.Public && !this.canAddImage()) ||
				(selectedFolder &&
					selectedFolder.Public &&
					!this.canAddPublishingImage())
			)
				return null;

			return (
				<div
					className="editCaptionContainer"
					onClick={(event) => this.onEditImage(event, image)}
					title="Edit caption"
					role="button"
					aria-label="Edit caption for this image"
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							this.onEditImage(e, image);
						}
					}}
				>
					<FontAwesome name="pencil" className="editCaptionIcon" />
				</div>
			);
		})();

		const imageDate = moment(image.date);
		const dateText = imageDate.format("MMMM D, YYYY");
		const timeText = imageDate.format("hh:mm A");
		const userName =
			image.user && image.user.displayName ? image.user.displayName : "Unknown";

		return (
			<div
				key={index}
				className="imageLibraryImageContainer"
				style={{
					border: isSelected ? "3px solid #5c90df" : "3px solid transparent",
					borderRadius: "8px",
				}}
			>
				<div
					className="topSection"
					style={{ ...this.getImageStyle(image.uri) }}
				>
					{checkbox}
					{deleteButton}
					{editCaptionButton}
					{viewButton}
				</div>
				<div className="bottomSection">
					{image.caption && <div className="imageCaption">{image.caption}</div>}
					<div className="imageDate">{`Uploaded ${dateText} • ${timeText}`}</div>
					<div className="imageBy">{`By ${userName}`}</div>
				</div>
			</div>
		);
	}

	renderPublishedImages() {
		const publishingFolder = this.props.imageLibrary.find(
			(folder) => folder.Public,
		);
		if (!publishingFolder) return null;

		const publishingImages = _.take(
			_.orderBy(publishingFolder.Images, "date", "desc"),
			20,
		);
		if (publishingImages.length === 0) return null;

		return (
			<div className="imageLibraryPublishedImages">
				<div className="title">LAST PUBLISHED IMAGES</div>
				<div className="publishedImages">
					{publishingImages.map((image, index) => {
						return this.renderImage(
							image,
							index,
							false,
							publishingImages,
							publishingFolder.Name,
						);
					})}
				</div>
			</div>
		);
	}

	renderRight() {
		const { selectedFolder, selectedImages } = this.state;

		const title = (() => {
			if (selectedFolder) {
				return (
					<div className={`libraryTitle ${styles.breadcrumbTitle}`}>
						<span
							onClick={this.onSelectRoot}
							className={styles.folderTitleClickable}
						>
							Folders
						</span>
						<FontAwesome
							name="chevron-right"
							className={styles.breadcrumbSeparator}
						/>
						<span>{selectedFolder.Name}</span>
					</div>
				);
			}
			return <div className="libraryTitle">Folders</div>;
		})();

		const search = (() => {
			if (selectedFolder) return null;

			return (
				<div className="searchFolder">
					<FontAwesome name="search" className="searchIcon" />
					<GenericInput
						id="searchFolderInput"
						type="text"
						placeholder="Search folder"
						onChange={this.onSearchChange}
						className={styles.searchContainer}
						value={this.state.searchText}
						textWrapperclassName={styles.searchInputWrapper}
					/>
				</div>
			);
		})();

		const addNew = (() => {
			if (selectedFolder) {
				const canAdd =
					(!selectedFolder.Public && this.canAddImage()) ||
					(selectedFolder.Public && this.canAddPublishingImage());

				return (
					<div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
						{canAdd && (
							<AddButton onClick={this.onUploadImage} text="UPLOAD IMAGE" />
						)}
						{selectedImages.length > 0 && (
							<Button
								buttonType="secondary"
								onClick={() => {
									this.setState({ deletingImage: selectedImages });
								}}
								isActive
								leftIcon="trash"
							>
								Delete selected images ({selectedImages.length})
							</Button>
						)}
					</div>
				);
			}
			return null;
		})();

		const content = (() => {
			if (selectedFolder) {
				const folder = this.getFolderFromProps();
				const images = folder ? folder.Images : [];
				return images.map((image, index) => {
					return this.renderImage(image, index, true, images, folder.Name);
				});
			}

			const { searchText } = this.state;
			const { imageLibrary } = this.props;
			const folders = _.isEmpty(searchText)
				? imageLibrary
				: imageLibrary.filter((folder) =>
						folder.Name.toLowerCase().includes(searchText.toLowerCase()),
					);
			return folders.map((folder, index) => {
				return folder.Public
					? this.renderPublicFolder(folder, index)
					: this.renderFolder(folder, index);
			});
		})();

		return (
			<div
				className={`imageLibrary ${this.props.loading ? styles.loading : ""}`}
			>
				<div className="imageLibraryTitleSection">
					{title}
					{search}
				</div>
				{addNew}
				{_.isNil(selectedFolder) && this.renderPublishedImages()}
				<div className="imageLibraryContents">{content}</div>
				{this.props.loading && this.renderLoadingOverlay()}
			</div>
		);
	}

	renderEditingOverlay() {
		return (
			<div
				className="hub-editingOverlay"
				onClick={this.onCancelEditingFolder}
			/>
		);
	}

	renderDeleteFolderConfirm() {
		return (
			<WarningPopup
				text={`Are you sure you want to delete ${this.state.deletingFolder.Name}?`}
				smallText
				buttons={[
					{
						type: "",
						onClick: this.onConfirmDeleteFolder,
						text: "Delete",
					},
					{
						type: "outlined",
						onClick: this.onCancelDeleteFolder,
						text: "Close",
					},
				]}
			/>
		);
	}

	renderDeleteImageConfirm() {
		const isBulkDelete = Array.isArray(this.state.deletingImage);
		const count = isBulkDelete ? this.state.deletingImage.length : 1;
		const message = `Are you sure you want to delete ${count} image${count > 1 ? "s" : ""}?`;

		return (
			<WarningPopup
				text={message}
				smallText
				buttons={[
					{
						type: "primary",
						onClick: this.onConfirmDeleteImage,
						text: "Delete",
					},
					{
						type: "outlined",
						onClick: this.onCancelDeleteImage,
						text: "Close",
					},
				]}
			/>
		);
	}

	renderImageViewer() {
		const { viewingImage } = this.state;
		if (!viewingImage) return null;

		return (
			<ImageViewer
				images={viewingImage.list}
				index={viewingImage.index}
				title={viewingImage.title}
				onChangeIndex={this.onChangeViewingIndex}
				onClose={this.onCloseImageViewer}
			/>
		);
	}

	renderEditCaption() {
		return (
			<Popup
				title="Edit Caption"
				minWidth="500px"
				buttons={[
					{
						type: "primary",
						onClick: this.onSaveCaption,
						isActive: true,
						text: "Save",
					},
					{
						type: "tertiary",
						onClick: this.onCancelEditCaption,
						isActive: true,
						text: "Cancel",
					},
				]}
				hasPadding
			>
				<GenericInput
					id="editCaptionInput"
					type="text"
					placeholder="Enter caption"
					value={this.state.editCaptionText}
					onChange={this.onChangeEditCaption}
				/>
				<p className={styles.captionHelpText}>
					Type words to show below your photo.
				</p>
			</Popup>
		);
	}

	renderLoadingOverlay() {
		return (
			<div className={styles.loadingOverlay}>
				<FontAwesome name="spinner fa-pulse fa-fw" className={styles.spinner} />
				<p>Loading images...</p>
			</div>
		);
	}

	renderImageUploader() {
		const { imagesUploaded } = this.state;
		return (
			<Popup
				title="Upload Image"
				buttons={[
					{
						type: "primary",
						onClick: this.onSaveUploadImage,
						isActive:
							this.state.imagesUploaded.length > 0 &&
							!this.state.imagesUploaded.some(
								(img) => img.caption.length > 150,
							) &&
							!this.state.uploadingImages,
						text: this.state.uploadingImages ? "Uploading..." : "Save",
						disabled: this.state.uploadingImages,
					},
					{
						type: "tertiary",
						onClick: this.onCancelUploadImage,
						isActive: true,
						text: "Cancel",
						disabled: this.state.uploadingImages,
					},
				]}
				minWidth="80vw"
				minHeight="80vh"
				hasPadding
			>
				{/* Image Cards Grid */}
				<div className={styles.uploadSection}>
					{this.state.uploadingImages && (
						<div className={styles.uploadingOverlay}>
							<FontAwesome
								name="spinner fa-pulse fa-fw"
								className={styles.spinner}
							/>
							<p>Uploading images to server...</p>
						</div>
					)}

					{/* Responsive Grid of Cards */}
					<div className={styles.imageCardsGrid}>
						{/* Upload Dropzone - first card in grid, no wrapper */}
						<Dropzone
							accept={{ "image/jpeg": [], "image/png": [] }}
							onDrop={this.onDropMoreImages}
						>
							{({ getRootProps, getInputProps }) => (
								<div {...getRootProps()} className={styles.uploadDropzone}>
									<FontAwesome
										name="cloud-upload"
										className={styles.uploadDropzoneIcon}
									/>
									<span className={styles.uploadDropzoneTitle}>
										Upload Images
									</span>
									<span className={styles.uploadDropzoneSubtitle}>
										or drag & drop here
									</span>
									<input {...getInputProps()} style={{ display: "none" }} />
								</div>
							)}
						</Dropzone>

						{imagesUploaded.map((image, index) => (
							<div
								key={index}
								className={styles.imageCard}
								onMouseEnter={(e) => {
									e.currentTarget.style.boxShadow =
										"0 4px 16px rgba(103, 125, 169, 0.15)";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.boxShadow =
										"0 2px 8px rgba(103, 125, 169, 0.1)";
								}}
							>
								{/* Image Thumbnail */}
								<div className={styles.imageThumbnailContainer}>
									<img
										src={get1400(image.uri)}
										alt={`Upload ${index + 1}`}
										className={styles.imageThumbnail}
									/>
								</div>

								{/* Action Buttons */}
								<div className={styles.actionButtonsContainer}>
									{/* Remove Button */}
									<div
										onClick={() => this.onRemoveUploadedImage(index)}
										className={styles.removeButton}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = "#ffe0e0";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = "#fff5f5";
										}}
										title="Remove this image"
									>
										<FontAwesome
											name="trash"
											className={styles.removeButtonIcon}
										/>
									</div>

									{/* Download Button */}
									<a
										href={image.uri}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.downloadButton}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = "#e8ecf0";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = "#f5f7fa";
										}}
										title="Download this image"
									>
										<FontAwesome
											name="download"
											className={styles.downloadButtonIcon}
										/>
									</a>
								</div>

								{/* Caption Input */}
								<div className={styles.captionInputSection}>
									<GenericInput
										type="text"
										placeholder={`Write a caption for this image...`}
										value={image.caption}
										onChange={(event) =>
											this.onChangeUploadCaption(index, event.target.value)
										}
									/>
								</div>

								{/* Character Counter */}
								<div className={styles.characterCounter}>
									<span>
										{image.caption.length > 150
											? `${150 - image.caption.length} characters over limit`
											: `${150 - image.caption.length} characters remaining`}
									</span>
									<span
										className={
											image.caption.length > 150
												? styles.characterCountOver
												: styles.characterCountNormal
										}
									>
										{image.caption.length} / 150
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</Popup>
		);
	}

	render() {
		const {
			editingFolder,
			deletingFolder,
			deletingImage,
			uploadingImage,
			editingImage,
			viewingImage,
		} = this.state;

		return (
			<div className="hub-wrapperContainer">
				{this.renderLeftBar()}
				<div className="hub-headerContentWrapper">
					<Header />
					<div className="hub-contentWrapper">{this.renderRight()}</div>
					{!_.isNil(editingFolder) && this.renderEditingOverlay()}
					{!_.isNil(deletingFolder) && this.renderDeleteFolderConfirm()}
					{!_.isNil(deletingImage) && this.renderDeleteImageConfirm()}
					{uploadingImage && this.renderImageUploader()}
					{!_.isNil(editingImage) && this.renderEditCaption()}
					{!_.isNil(viewingImage) && this.renderImageViewer()}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const { auth, media } = state;
	return {
		auth,
		imageLibrary: media.imageLibrary.filter((folder) => folder.RowId),
		loading: media.loading,
	};
};

export default connect(mapStateToProps, {
	imageLibraryUpdate,
	imageFolderUpdated,
	imageFolderRemoved,
})(withRouter(ImageLibraryHub));
